import { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { DispositiveProvisionsFormRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { _getDefaultedKindForRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsFormRecipients/hooks/useDefaultRecipientTransferTaxKind';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { PathsOf } from '@/types/subform';

import {
  BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE,
  BeneficiaryDesignationsFormShape,
} from '../BeneficiaryDesignationsForm.types';
import { useBdgDyingClientId } from './useBdgDyingClientId';
import { useBeneficiaryDesignationRecipientOptions } from './useBeneficiaryDesignationRecipientOptions';

/**
 * BDG analog of the DP form's `useDefaultRecipientTransferTaxKind`. Defaults a
 * beneficiary's recipient type from the recipient's relationship to the
 * account's decedent (e.g. a child defaults to "Generation 2 or other
 * individual"), reusing the DP form's pure `_getDefaultedKindForRecipient`
 * resolver but sourcing its inputs from the BDG form's contexts rather than
 * DispositiveProvisionsContext (which the BDG form intentionally avoids).
 */
export function useDefaultBdgRecipientTransferTaxKind({
  tier,
  index,
  recipientLists,
}: {
  tier: 'primary' | 'contingent';
  index: number;
  recipientLists: ReturnType<
    typeof useBeneficiaryDesignationRecipientOptions
  >['recipientLists'];
}) {
  const {
    setValue,
    control,
    formState: { isLoading },
  } = useFormContext<BeneficiaryDesignationsFormShape>();

  const { primaryClients } = useHouseholdDetailsContext();
  const dyingClientId = useBdgDyingClientId();

  const transferTaxKindFieldName =
    `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}.recipients.${index}.transferTaxKind` as const satisfies PathsOf<BeneficiaryDesignationsFormShape>;

  // Watch the whole array under a constant `name` and index in, rather than
  // watching `recipients.${index}` directly: a `useWatch` whose name embeds the
  // index lags a render behind after a reorder (the index prop updates before
  // the subscription re-reads), so the row would briefly read another row's
  // value. Reading from one array snapshot keeps the index consistent.
  const recipients = useWatch({
    control,
    name: `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}.recipients`,
  }) as DispositiveProvisionsFormRecipient[] | undefined;

  const recipient = recipients?.[index];
  const recipientTransferTaxKind = recipient?.transferTaxKind;

  // The recipients list is a `useFieldArray`, so a row's index is not stable
  // (add/remove/reorder shifts it). We track defaulting state in refs — stable
  // per recipient because the list keys each row by its RHF `_id` — instead of
  // index-positional form state (`defaultValues[index]` or per-index dirty),
  // which would let a shifted row inherit a former sibling's value.

  // Whether the user (or a saved record) owns this row's type; once true we
  // stop auto-defaulting. Seeded from the value the row first rendered with.
  const userOwnsKindRef = useRef(Boolean(recipientTransferTaxKind));

  // The last value we set programmatically; when the watched value diverges
  // from it, the user changed the type by hand.
  const lastAutoSetKindRef = useRef<
    typeof recipientTransferTaxKind | undefined
  >(undefined);

  const defaultedKind =
    dyingClientId && recipient
      ? _getDefaultedKindForRecipient(recipient, {
          ...recipientLists,
          primaryClients: primaryClients ?? [],
          dyingClientId,
          hasStateTax: false,
        })
      : null;

  useEffect(() => {
    if (isLoading) return;

    if (
      recipientTransferTaxKind &&
      recipientTransferTaxKind !== lastAutoSetKindRef.current
    ) {
      userOwnsKindRef.current = true;
    }

    if (userOwnsKindRef.current) return;
    if (defaultedKind === null) return;
    if (recipientTransferTaxKind === defaultedKind) return;

    lastAutoSetKindRef.current = defaultedKind;
    // shouldDirty: false keeps us defaulting programmatically until the user
    // takes over (tracked via userOwnsKindRef).
    setValue(transferTaxKindFieldName, defaultedKind, { shouldDirty: false });
  }, [
    defaultedKind,
    isLoading,
    recipientTransferTaxKind,
    setValue,
    transferTaxKindFieldName,
  ]);
}
